/**
 * DWV Worker Asset Resolver
 *
 * Intercepts Web Worker instantiation to redirect worker script URLs
 * from relative paths to absolute paths under the static bundles directory.
 *
 * This is necessary because DWV library workers are copied to the static
 * directory during build, but the library tries to load them relatively.
 *
 * Detection is based on known DWV worker filenames. Any worker URL containing
 * one of these filenames will be redirected to the static bundles directory.
 *
 * Example:
 *  Original scriptURL: 'http://localhost:6543/todo/assets/workers/jpegbaseline.worker.min.js'
 *  Resolved scriptURL: '/static/bundles/vendor/workers/jpegbaseline.worker.min.js'
 */

const WORKER_BASE_PATH = '/static/bundles/vendor/workers/';

// Known DWV worker filenames
const DWV_WORKER_NAMES = [
  'jpeg2000.worker.min.js',
  'jpegbaseline.worker.min.js',
  'jpegloss.worker.min.js',
  'labeling.worker.min.js',
  'rle.worker.min.js',
];

/**
 * Sets up the Worker constructor override to redirect worker paths
 * Should be called once before any DWV components are initialized
 */
export function setupDWVWorkerResolver() {
  if (!window.Worker) {
    console.warn('Web Workers are not supported in this environment');
    return;
  }

  // Avoid double-initialization
  if (window.__DWV_WORKER_RESOLVER_INITIALIZED__) {
    return;
  }

  const OriginalWorker = window.Worker;

  window.Worker = function(scriptURL, options) {
    const resolvedURL = resolveWorkerURL(scriptURL, options);
    return new OriginalWorker(resolvedURL, options);
  };

  // Preserve the original Worker's prototype and static properties
  Object.setPrototypeOf(window.Worker.prototype, OriginalWorker.prototype);
  Object.setPrototypeOf(window.Worker, OriginalWorker);

  window.__DWV_WORKER_RESOLVER_INITIALIZED__ = true;
}

/**
 * Resolves a worker URL to the correct static path if it matches DWV worker filenames
 * @param {string | URL} scriptURL - The original worker script URL
 * @param {WorkerOptions} options - Worker options that may contain a name (not used here)
 * @returns {string} - The resolved URL
 */
function resolveWorkerURL(scriptURL, options) {
  const url = String(scriptURL);

  // Check if the URL contains any known DWV worker filename
  const matchedWorker = DWV_WORKER_NAMES.find(workerName => url.includes(workerName));

  if (!matchedWorker) {
    console.warn(`DWV Worker Resolver: No matching worker found for URL: ${url}`);
    return url;
  }

  const resolvedPath = `${WORKER_BASE_PATH}${matchedWorker}`;
  return resolvedPath;
}
